import { Box, Switch, TextField, Typography, IconButton, Button, MenuItem, Select, FormControl, InputLabel, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import NumbersIcon from "@mui/icons-material/Numbers";
import SubjectIcon from "@mui/icons-material/Subject";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import EventIcon from "@mui/icons-material/Event";
import ArrowDropDownCircleIcon from "@mui/icons-material/ArrowDropDownCircle";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } };
const accentText = (a) => ({ ...fieldSx, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const FIELD_TYPES = [
  { v: "text", l: "Text", icon: TextFieldsIcon },
  { v: "email", l: "Email", icon: EmailIcon },
  { v: "phone", l: "Phone", icon: PhoneIcon },
  { v: "number", l: "Number", icon: NumbersIcon },
  { v: "textarea", l: "Long text", icon: SubjectIcon },
  { v: "select", l: "Dropdown", icon: ArrowDropDownCircleIcon },
  { v: "radio", l: "Radio", icon: RadioButtonCheckedIcon },
  { v: "checkbox", l: "Checkbox", icon: CheckBoxIcon },
  { v: "date", l: "Date", icon: EventIcon },
];

const newField = (type = "text") => ({
  name: "",
  type,
  label: "",
  placeholder: "",
  required: false,
  options: type === "select" || type === "radio" ? [""] : [],
});

const FormTab = ({ skipOption, setSkipOption, errorMessage, setErrorMessage, settings = {}, setSettings, accentColor = "#14b8a6" }) => {
  const fields = (settings.fields || []).map((f) => ({ options: [], ...f }));
  const submitText = settings.submitText || "Submit";

  const update = (next) => setSettings({ ...settings, fields: next });

  const addField = (type = "text") => update([...fields, newField(type)]);
  const removeField = (i) => update(fields.filter((_, idx) => idx !== i));
  const changeField = (i, patch) => {
    const u = [...fields];
    const merged = { ...u[i], ...patch };
    if (patch.type && (patch.type === "select" || patch.type === "radio") && !Array.isArray(merged.options)) {
      merged.options = [""];
    }
    if (patch.type && patch.type !== "select" && patch.type !== "radio") {
      delete merged.options;
    }
    u[i] = merged;
    update(u);
  };

  const moveField = (i, dir) => {
    const u = [...fields];
    const j = i + dir;
    if (j < 0 || j >= u.length) return;
    [u[i], u[j]] = [u[j], u[i]];
    update(u);
  };

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip the entire form</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Form Fields</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Add the fields visitors should fill. All values are stored as one JSON object.</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mb: 1.5 }}>
          {FIELD_TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <Chip
                key={t.v}
                icon={<Icon sx={{ fontSize: 14 }} />}
                label={t.l}
                size="small"
                onClick={() => addField(t.v)}
                sx={{ fontSize: 11, fontWeight: 600, bgcolor: `${accentColor}10`, color: accentColor, border: `1px solid ${accentColor}30`, "&:hover": { bgcolor: `${accentColor}18` }, "& .MuiChip-icon": { color: accentColor } }}
              />
            );
          })}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {fields.map((f, i) => {
            const FieldIcon = FIELD_TYPES.find((t) => t.v === f.type)?.icon || TextFieldsIcon;
            const showOptions = f.type === "select" || f.type === "radio";
            return (
              <Box key={i} sx={{ p: 1.5, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, flex: 1 }}>
                    <DragIndicatorIcon sx={{ fontSize: 14, color: "#bbb" }} />
                    <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: `${accentColor}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FieldIcon sx={{ fontSize: 13, color: accentColor }} />
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.label || `Field ${i + 1}`} <Typography component="span" sx={{ fontSize: 10, color: "#9ca3af" }}>· {FIELD_TYPES.find((t) => t.v === f.type)?.l}</Typography>
                    </Typography>
                    {f.required && <Chip label="Required" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626" }} />}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, flexShrink: 0 }}>
                    <IconButton size="small" onClick={() => moveField(i, -1)} disabled={i === 0} sx={{ color: "#9ca3af", "&:hover": { color: accentColor } }}>
                      <Typography sx={{ fontSize: 12, lineHeight: 1 }}>↑</Typography>
                    </IconButton>
                    <IconButton size="small" onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} sx={{ color: "#9ca3af", "&:hover": { color: accentColor } }}>
                      <Typography sx={{ fontSize: 12, lineHeight: 1 }}>↓</Typography>
                    </IconButton>
                    <IconButton size="small" onClick={() => removeField(i)} sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 0.8 }}>
                  <TextField fullWidth size="small" label="Label" placeholder="Full name"
                    value={f.label || ""}
                    onChange={(e) => changeField(i, { label: e.target.value })}
                    sx={accentText(accentColor)} />
                  <TextField fullWidth size="small" label="Variable name" placeholder="fullName"
                    value={f.name || ""}
                    onChange={(e) => changeField(i, { name: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_") })}
                    sx={accentText(accentColor)} />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px", gap: 0.8, alignItems: "center" }}>
                  <TextField fullWidth size="small" label="Placeholder" placeholder="Enter your name"
                    value={f.placeholder || ""}
                    onChange={(e) => changeField(i, { placeholder: e.target.value })}
                    sx={fieldSx} />
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ "&.Mui-focused": { color: accentColor } }}>Type</InputLabel>
                    <Select
                      value={f.type}
                      label="Type"
                      onChange={(e) => changeField(i, { type: e.target.value })}
                      sx={{ borderRadius: 2, bgcolor: "#fff", fontSize: 12, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accentColor } }}
                    >
                      {FIELD_TYPES.map((t) => (
                        <MenuItem key={t.v} value={t.v} sx={{ fontSize: 12 }}>{t.l}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
                    <Typography sx={{ fontSize: 10, color: "#6b7280", fontWeight: 600 }}>Required</Typography>
                    <Switch checked={!!f.required} onChange={(e) => changeField(i, { required: e.target.checked })} size="small" />
                  </Box>
                </Box>

                {showOptions && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: "#fafbff", borderRadius: 1.5, border: "1px solid #f3f4f6" }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#6b7280", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}>Options</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {(f.options || [""]).map((opt, oi) => (
                        <Box key={oi} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <TextField fullWidth size="small" placeholder={`Option ${oi + 1}`}
                            value={opt}
                            onChange={(e) => {
                              const u = [...(f.options || [])];
                              u[oi] = e.target.value;
                              changeField(i, { options: u });
                            }}
                            sx={fieldSx} />
                          <IconButton size="small" onClick={() => {
                            const u = (f.options || []).filter((_, idx) => idx !== oi);
                            changeField(i, { options: u.length ? u : [""] });
                          }} sx={{ color: "#9ca3af" }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={() => {
                      changeField(i, { options: [...(f.options || []), ""] });
                    }} sx={{ mt: 0.5, textTransform: "none", fontSize: 10, color: accentColor }}>
                      Add option
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {fields.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center", color: "#9ca3af", fontSize: 12, border: "1.5px dashed #e5e7eb", borderRadius: 2 }}>
            No fields yet. Click any field type above to add one.
          </Box>
        )}
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5, color: "#1f2937" }}>Submit Button</Typography>
        <TextField fullWidth size="small" label="Button text" placeholder="Submit"
          value={submitText}
          onChange={(e) => setSettings({ ...settings, submitText: e.target.value })}
          sx={accentText(accentColor)} />
        <Typography sx={{ fontSize: 10, color: "#6b7280", mt: 0.8 }}>All filled values will be saved as a single JSON object under the question's variable name.</Typography>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Please fill all required fields" value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={fieldSx} />
      </Box>
    </Box>
  );
};

export default FormTab;
